import { memo, useCallback, useMemo } from "react"
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import { Checkbox, Link, Box, Stack, Typography } from "@mui/material"

function DataRow({ dataItem: { stock_no, ct, color, clarity, cut, polish, symm, lab, report_no, disc, total_dollar }, selected, onToggle, isMobile }) {
    const handleRowClick = useCallback(() => {
        onToggle(stock_no)
    }, [onToggle, stock_no])

    const handleCheckboxToggle = useCallback((event) => {
        event.stopPropagation()
        onToggle(stock_no)
    }, [onToggle, stock_no])

    const handleLinkClick = useCallback((event) => {
        event.stopPropagation()
    }, [])

    const formatNumber = useCallback((value, { prefix = "", suffix = "", decimals = 2 } = {}) => {
        const numberValue = typeof value === "number" ? value : Number(value)
        if (!Number.isFinite(numberValue)) {
            return "—"
        }
        return `${prefix}${numberValue.toFixed(decimals)}${suffix}`
    }, [])

    const discountLabel = useMemo(() => {
        const numberValue = typeof disc === "number" ? disc : Number(disc)
        if (!Number.isFinite(numberValue)) {
            return "—"
        }
        return `${(-1 * numberValue).toFixed(2)}%`
    }, [disc])

    const totalDollarLabel = useMemo(
        () => formatNumber(total_dollar, { prefix: "$", decimals: 2 }),
        [formatNumber, total_dollar]
    )

    const caratLabel = useMemo(
        () => formatNumber(ct, { decimals: 2 }),
        [ct, formatNumber]
    )

    if (isMobile) {
        return (
            <TableRow
                hover
                selected={selected}
                sx={{ 
                  '&:last-child td, &:last-child th': { border: 0 },
                  cursor: 'pointer',
                  ...(selected && { bgcolor: 'action.selected' })
                }}
                onClick={handleRowClick}
            >
                <TableCell sx={{ py: 2, px: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        {/* Left Column */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {/* Subcolumn 1 */}
                            <Stack spacing={0.5}>
                                <Typography variant="body2" fontWeight={600}>{stock_no}</Typography>
                                <Link 
                                    href={`https://www.gia.edu/report-check?locale=en_US&reportno=${report_no}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={handleLinkClick}
                                    sx={{ color: 'primary.main', fontSize: '0.75rem', textDecoration: 'none' }}
                                >
                                    {report_no}
                                </Link>
                            </Stack>
                            {/* Subcolumn 2 */}
                            <Stack spacing={0.5}>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                                    {caratLabel}  <Box component="span" sx={{ ml: 1 }}>{color}  {clarity}</Box>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500, letterSpacing: 1 }}>
                                    {cut}  {polish}  {symm}
                                </Typography>
                            </Stack>
                        </Box>

                        {/* Right Column */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            {/* Subcolumn 1 */}
                            <Stack spacing={0.5} alignItems="flex-end">
                                <Typography variant="body2" fontWeight={700} color={disc < 0 ? 'text.primary' : 'text.primary'}>
                                    {discountLabel}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    {totalDollarLabel}
                                </Typography>
                            </Stack>
                            {/* Subcolumn 2 */}
                            <Checkbox
                                checked={selected}
                                onChange={handleCheckboxToggle}
                                onClick={handleCheckboxToggle}
                                sx={{ p: 0 }}
                            />
                        </Box>
                    </Box>
                </TableCell>
            </TableRow>
        )
    }

    return (
        <TableRow
            hover
            selected={selected}
            sx={{ 
              '&:last-child td, &:last-child th': { border: 0 },
              cursor: 'pointer',
              ...(selected && { bgcolor: 'action.selected' })
            }}
            onClick={handleRowClick}
        >
            <TableCell padding="checkbox">
                <Checkbox
                    checked={selected}
                    onChange={handleCheckboxToggle}
                    onClick={handleCheckboxToggle}
                />
            </TableCell>
            <TableCell align="center">{stock_no}</TableCell>
            <TableCell align="center">
                <Link 
                    href={`https://www.gia.edu/report-check?locale=en_US&reportno=${report_no}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={handleLinkClick}
                    sx={{ color: 'primary.main' }}
                >
                    {report_no}
                </Link>
            </TableCell>
            <TableCell align="center">{caratLabel}</TableCell>
            <TableCell align="center">{color}</TableCell>
            <TableCell align="center">{clarity}</TableCell>
            <TableCell align="center">{cut}</TableCell>
            <TableCell align="center">{polish}</TableCell>
            <TableCell align="center">{symm}</TableCell>
            <TableCell align="center">{discountLabel}</TableCell>
            <TableCell align="center">{totalDollarLabel}</TableCell>
        </TableRow>
    )
}

export default memo(DataRow)
